import { useState, useRef } from "react";
import api from "../../api/client";

export default function NapImportModal({ onClose, onRefresh }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // upload | done
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState("");

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/nap-locator/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      setStep("done");
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const handleExportErrors = () => {
    if (!importResult?.error_rows?.length) return;
    // Exclude duplicate rows — only export validation/other errors
    const nonDuplicateRows = importResult.error_rows.filter(
      (r) => !(r.reason || "").includes("already exists in database")
    );
    if (!nonDuplicateRows.length) return;
    // Build CSV content
    const headers = ["Row", "NAP Code", "No of Ports", "Latitude", "Longitude", "Area", "Reason"];
    const csvRows = [headers.join(",")];
    for (const r of nonDuplicateRows) {
      const row = [
        r.row || "",
        `"${(r.nap_code || "").replace(/"/g, '""')}"`,
        r.no_of_ports ?? "",
        r.latitude ?? "",
        r.longitude ?? "",
        `"${(r.area || "").replace(/"/g, '""')}"`,
        `"${(r.reason || "").replace(/"/g, '""')}"`,
      ];
      csvRows.push(row.join(","));
    }
    const blob = new Blob(["\uFEFF" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nap_import_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setFile(null);
    setStep("upload");
    setImportResult(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Import NAP Locator Records</h2>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Upload XLSX File <span className="text-red-500">*</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            disabled={step !== "upload"}
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="block w-full text-sm disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-1">Only .xlsx files are accepted. Max 10MB.</p>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {importResult && (
          <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
            <p className="font-medium mb-2">Import Summary</p>
            <p className="text-gray-700">Total Rows Read: {importResult.total}</p>
            <p className="text-green-700">Successfully Imported: {importResult.inserted}</p>
            <p className="text-yellow-700">Skipped Duplicates: {importResult.skipped_duplicates}</p>
            {importResult.skipped_blank > 0 && (
              <p className="text-gray-500">Skipped Blank Rows: {importResult.skipped_blank}</p>
            )}
            <p className="text-red-600">Validation Errors: {importResult.invalid_rows}</p>

            {importResult.duplicate_details && importResult.duplicate_details.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto text-xs text-yellow-700 border-t pt-2">
                <p className="font-medium mb-1">Duplicate Details:</p>
                {importResult.duplicate_details.map((d, i) => (
                  <p key={i}>{d}</p>
                ))}
              </div>
            )}

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto text-xs text-red-600 border-t pt-2">
                <p className="font-medium mb-1">Row Errors:</p>
                {importResult.errors.map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}

            {importResult.error_rows && importResult.error_rows.filter(r => !(r.reason || "").includes("already exists in database")).length > 0 && (
              <div className="mt-3 border-t pt-2">
                <button
                  onClick={handleExportErrors}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                >
                  Export Errors ({importResult.error_rows.filter(r => !(r.reason || "").includes("already exists in database")).length} rows)
                </button>
                <p className="text-[10px] text-gray-400 mt-1">Download a CSV with error details for resolution.</p>
              </div>
            )}
          </div>
        )}

        {step === "done" && (
          <div className={`text-sm rounded px-3 py-2 mb-3 border ${
            importResult && (importResult.invalid_rows > 0 || importResult.skipped_duplicates > 0)
              ? "bg-yellow-50 border-yellow-300 text-yellow-700"
              : "bg-green-50 border-green-300 text-green-700"
          }`}>
            {importResult && importResult.inserted === 0
              ? "No new records were imported. All rows were either duplicates or had errors."
              : importResult && (importResult.invalid_rows > 0 || importResult.skipped_duplicates > 0)
                ? `Import completed. ${importResult.inserted} record(s) imported, ${importResult.skipped_duplicates + importResult.invalid_rows} row(s) skipped.`
                : "Import completed successfully."
            }
          </div>
        )}

        <div className="flex justify-end gap-2">
          {step === "upload" ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!file || importing}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {importing ? "Importing…" : "Import"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Import Another
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
